import { useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { Archive, BookOpenText, ChevronDown, ChevronUp, LockKeyhole, MessageSquare, Plus, UserRound } from 'lucide-react'
import { toast } from 'sonner'
import { classroomsAtom, isDarkAtom, isSessionsLoadingAtom, sessionsAtom } from '../state/store'
import { getClassroomPalette } from '../lib/classroom-theme'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { joinClassByCode } from '../api/backend'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'
import { Input } from './ui/input'
import { Button } from './ui/button'

export const CLASS_CARD_TITLE_CLASSNAME = 'truncate font-heading text-lg leading-tight'
export const CLASS_CARD_TOPIC_ROW_CLASSNAME = 'mt-2.5 flex min-h-[1.625rem] max-w-full flex-nowrap items-center gap-1.5 overflow-hidden'
export type StudentClassroomAccessAction = 'open' | 'join' | 'blocked'

export function getStudentClassroomAccessAction(classroom: {
  status: 'IN_PROGRESS' | 'ENDED' | 'PREPARING'
  isJoined?: boolean
}): StudentClassroomAccessAction {
  if (classroom.isJoined) return 'open'
  if (classroom.status === 'ENDED') return 'blocked'
  return 'join'
}

function getHeroForegroundMode(backgroundColor: string) {
  const match = backgroundColor.match(/hsl\(\s*\d+\s+\d+%\s+(\d+)%\s*\)/i)
  const lightness = match ? Number(match[1]) : 50
  return lightness >= 68 ? 'dark' : 'light'
}

function maskClassCode(value: string) {
  return value.replace(/[A-Z0-9]/gi, '•')
}

function ClassCard({
  classroom,
  title,
  classCode,
  teacherName,
  topics,
  chats,
  paletteSeed,
  ended,
  onOpen,
}: {
  classroom: { id: string; isJoined?: boolean }
  title: string
  classCode: string
  teacherName?: string | null
  topics: string[]
  chats: number
  paletteSeed: { id?: string | null; title?: string | null; icon?: string | null }
  ended?: boolean
  onOpen: () => void
}) {
  const isDark = useAtomValue(isDarkAtom)
  const palette = getClassroomPalette(paletteSeed)
  const heroBg = isDark ? palette.dark : palette.light
  const heroMode = getHeroForegroundMode(heroBg)
  const lowerClass = isDark ? 'bg-[#181a1f] text-white' : 'bg-[#f2ede3] text-[#231f1d]'
  const codePanelClass = isDark ? 'bg-[#293142] text-white' : 'bg-[#dfe5f1] text-[#231f1d]'
  const heroTitleClass = heroMode === 'dark' ? 'text-[#202114]' : 'text-white'
  const heroMetaClass = heroMode === 'dark' ? 'text-[#202114]/68' : 'text-white/68'
  const topicClass = heroMode === 'dark'
    ? 'border-black/12 bg-black/8 text-[#202114]/80'
    : 'border-white/18 bg-white/10 text-white/84'
  const metaTextClass = isDark ? 'text-white/62' : 'text-[#231f1d]/62'
  const displayCode = classroom.isJoined ? classCode : maskClassCode(classCode)
  const heroStatusClass = heroMode === 'dark'
    ? 'border-black/14 bg-white/38 text-[#202114]'
    : 'border-black/25 bg-black/12 text-white'
  const visibleTopics = topics.slice(0, 2)
  const hiddenTopicCount = Math.max(0, topics.length - visibleTopics.length)

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`grid aspect-[7/6] w-full grid-rows-[40%_1fr] overflow-hidden rounded-[1.4rem] border-[3px] border-nb-border text-left nb-shadow transition-transform hover:-translate-y-0.5 ${lowerClass}`}
    >
      <div
        className="relative overflow-hidden px-5 pb-3 pt-4"
        style={{ backgroundColor: heroBg }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_30%),linear-gradient(180deg,rgba(0,0,0,0.06),rgba(0,0,0,0.18))]" />
        {ended ? (
          <span className={`absolute right-5 top-4 z-10 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[0.68rem] font-heading tracking-[0.08em] shadow-[0_2px_0_0_rgba(0,0,0,0.22)] ${heroStatusClass}`}>
            <Archive className="size-3.5" />
            Ended
          </span>
        ) : null}
        <div className="relative min-w-0">
          <h3 className={`${CLASS_CARD_TITLE_CLASSNAME} ${ended ? 'pr-[6.75rem]' : ''} ${heroTitleClass}`} title={title}>
            {title}
          </h3>
          <div className={CLASS_CARD_TOPIC_ROW_CLASSNAME}>
            {topics.length > 0 ? (
              <>
                {visibleTopics.map((topic) => (
                  <span
                    key={topic}
                    className={`inline-flex min-w-0 max-w-[7rem] items-center rounded-full border px-2.5 py-1 text-[0.62rem] font-medium ${topicClass}`}
                    title={topic}
                  >
                    <span className="block min-w-0 truncate">{topic}</span>
                  </span>
                ))}
                {hiddenTopicCount > 0 ? (
                  <span
                    className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[0.62rem] font-medium ${topicClass}`}
                    title={`${hiddenTopicCount} more topic${hiddenTopicCount === 1 ? '' : 's'}`}
                  >
                    +{hiddenTopicCount}
                  </span>
                ) : null}
              </>
            ) : (
              <span className={`text-[0.78rem] ${heroMetaClass}`}>No topics yet</span>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 py-4">
        <div className={`rounded-[0.8rem] border-[3px] border-black px-4 py-3 ${codePanelClass}`}>
          <p className={`${isDark ? 'text-white/42' : 'text-[#231f1d]/44'} text-[0.68rem] uppercase tracking-[0.34em]`}>
            Class Code
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            {!classroom.isJoined ? <LockKeyhole className="size-4 shrink-0 opacity-70" /> : null}
            <p
              className="select-none font-bold leading-none tracking-[0.06em]"
              style={{ fontFamily: '"DM Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
              title={classroom.isJoined ? classCode : 'Join class to reveal code'}
            >
              {displayCode}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-start justify-between gap-3">
          <div className={`min-w-0 flex items-center gap-1.5 text-[0.82rem] ${metaTextClass}`}>
            <UserRound className="size-3.5 shrink-0" />
            <p className="line-clamp-1">{teacherName || 'No teacher assigned'}</p>
          </div>
          <div className={`shrink-0 text-[0.82rem] ${metaTextClass}`}>
            {classroom.isJoined ? (
              <span className="inline-flex items-center gap-1.5">
                <MessageSquare className="size-3.5" />
                {chats} {chats === 1 ? 'chat' : 'chats'}
              </span>
            ) : (
              'Code required'
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

export function StudentHome({ loading = false }: { loading?: boolean }) {
  const classrooms = useAtomValue(classroomsAtom)
  const setClassrooms = useSetAtom(classroomsAtom)
  const sessions = useAtomValue(sessionsAtom)
  const isSessionsLoading = useAtomValue(isSessionsLoadingAtom)
  const isDark = useAtomValue(isDarkAtom)
  const { getAccessToken } = useAuth()
  const { memberRole } = useWorkspace()
  const navigate = useNavigate()
  const [allDoneOpen, setAllDoneOpen] = useState(false)
  const [joinDialogOpen, setJoinDialogOpen] = useState(false)
  const [classCodeInput, setClassCodeInput] = useState('')
  const [isJoining, setIsJoining] = useState(false)

  const activeClasses = useMemo(
    () => classrooms.filter((item) => item.status === 'IN_PROGRESS'),
    [classrooms],
  )
  const doneClasses = useMemo(
    () => classrooms.filter((item) => item.status === 'ENDED'),
    [classrooms],
  )
  const skeletonTitleClass = isDark ? 'bg-white/18' : 'bg-white/55'
  const skeletonSubtitleClass = isDark ? 'bg-white/14' : 'bg-white/40'
  const skeletonTopicClass = isDark ? 'bg-white/12' : 'bg-white/35'
  const skeletonMetaClass = isDark ? 'bg-white/12' : 'bg-black/6'
  const skeletonCodePanelClass = isDark ? 'bg-[#293142]' : 'bg-[#eef2fa]'
  const skeletonLowerClass = isDark ? 'bg-[#181a1f]' : 'bg-[#f2ede3]'

  const SkeletonCard = () => (
    <div className="grid aspect-[7/6] grid-rows-[40%_1fr] overflow-hidden rounded-[1.4rem] border-[3px] border-nb-border nb-shadow">
      <div className={`overflow-hidden px-5 pb-3 pt-4 ${isDark ? 'bg-[#384822]' : 'bg-[#c5d9a5]'}`}>
        <SkeletonBar className={`h-7 w-1/2 ${skeletonTitleClass}`} />
        <SkeletonBar className={`mt-2 h-4 w-1/3 ${skeletonSubtitleClass}`} />
        <div className="mt-3 flex max-w-full gap-1.5 overflow-hidden">
          <SkeletonBar className={`h-5 w-12 shrink-0 rounded-full ${skeletonTopicClass}`} />
          <SkeletonBar className={`h-5 w-14 shrink-0 rounded-full ${skeletonTopicClass}`} />
        </div>
      </div>
      <div className={`px-5 py-4 ${skeletonLowerClass}`}>
        <div className={`rounded-[0.8rem] border-[3px] border-black px-4 py-3 ${skeletonCodePanelClass}`}>
          <SkeletonBar className={`h-3 w-24 ${skeletonMetaClass}`} />
          <SkeletonBar className={`mt-2 h-7 w-32 ${skeletonMetaClass}`} />
        </div>
        <div className="mt-4 flex items-start justify-between gap-3">
          <SkeletonBar className={`h-4 w-20 ${skeletonMetaClass}`} />
          <SkeletonBar className={`h-4 w-24 ${skeletonMetaClass}`} />
        </div>
      </div>
    </div>
  )

  const openClassroom = async (classroom: (typeof classrooms)[number]) => {
    if (memberRole === 'TEACHER' || memberRole === 'ADMIN' || classroom.isJoined) {
      navigate(`/classes/${classroom.id}`)
      return
    }

    const action = getStudentClassroomAccessAction(classroom as {
      status: 'IN_PROGRESS' | 'ENDED' | 'PREPARING'
      isJoined?: boolean
    })

    if (action === 'open') {
      navigate(`/classes/${classroom.id}`)
      return
    }

    if (action === 'blocked') {
      toast.error('This class is not available to join right now.')
      return
    }

    toast.error('Use Join New Class to enter a class code.')
  }

  const handleJoinClassroom = async () => {
    const token = getAccessToken()
    if (!token || !classCodeInput.trim()) return

    setIsJoining(true)
    try {
      const result = await joinClassByCode(token, {
        classCode: classCodeInput.trim().toUpperCase(),
      })
      setClassrooms((prev) =>
        prev.some((item) => item.id === result.classroom.id)
          ? prev.map((item) =>
              item.id === result.classroom.id
                ? { ...item, ...result.classroom, isJoined: true }
                : item,
            )
          : [{ ...result.classroom, isJoined: true }, ...prev],
      )
      setClassCodeInput('')
      setJoinDialogOpen(false)
      toast.success('Class joined')
      navigate(`/classes/${result.classroom.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to join class'
      if (/invalid class code/i.test(message)) {
        toast.error('That class code does not match. Check the code and try again.')
      } else if (/not available to join right now/i.test(message) || /not accepting new students/i.test(message)) {
        toast.error('This class is not available to join right now.')
      } else {
        toast.error(message)
      }
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[920px] px-5 py-6">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-foreground">My Classes</h1>
            <p className="mt-2 text-sm text-foreground/60">
              Choose a class to start learning
            </p>
          </div>
          {memberRole === 'STUDENT' ? (
            <Button size="sm" onClick={() => setJoinDialogOpen(true)}>
              <Plus className="size-4" />
              Join New Class
            </Button>
          ) : null}
        </div>

        <section>
          <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-heading uppercase tracking-[0.18em] text-foreground/45">
            <BookOpenText className="size-4" />
            Happening Now!
          </div>
          {loading || isSessionsLoading ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : activeClasses.length === 0 ? (
            <div className="rounded-base border-2 border-dashed border-nb-border bg-bg p-10 text-sm text-foreground/45">
              No classes yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {activeClasses.map((classroom) => (
                <ClassCard
                  key={classroom.id}
                  classroom={classroom}
                  title={classroom.title}
                  classCode={classroom.classCode}
                  teacherName={classroom.teacherName}
                  topics={classroom.topics}
                  chats={sessions.filter((item) => item.classId === classroom.id).length}
                  paletteSeed={classroom}
                  onOpen={() => void openClassroom(classroom)}
                />
              ))}
            </div>
          )}
        </section>

        {doneClasses.length > 0 ? (
          <section className="mt-8">
            <button
              type="button"
              onClick={() => setAllDoneOpen((prev) => !prev)}
              className="mb-4 inline-flex items-center gap-2 text-[11px] font-heading uppercase tracking-[0.18em] text-foreground/45 transition-colors hover:text-foreground/65"
            >
              {allDoneOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
              Ended ({doneClasses.length})
            </button>
            {allDoneOpen ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {doneClasses.map((classroom) => (
                  <ClassCard
                    key={classroom.id}
                    classroom={classroom}
                    title={classroom.title}
                    classCode={classroom.classCode}
                    teacherName={classroom.teacherName}
                    topics={classroom.topics}
                    chats={sessions.filter((item) => item.classId === classroom.id).length}
                    paletteSeed={classroom}
                    ended
                    onOpen={() => void openClassroom(classroom)}
                  />
                ))}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>

      {memberRole === 'STUDENT' ? (
        <Dialog open={joinDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setJoinDialogOpen(false)
            setClassCodeInput('')
          }
        }}>
          <DialogContent className="max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Enter Class Code</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3">
              <p className="text-sm text-foreground/60">
                Enter a class code to join a new classroom.
              </p>
              <Input
                value={classCodeInput}
                onChange={(event) => setClassCodeInput(event.target.value.toUpperCase())}
                placeholder="ARTS-01"
              />
            </div>
            <DialogFooter>
              <Button variant="neutral" size="sm" onClick={() => {
                setJoinDialogOpen(false)
                setClassCodeInput('')
              }}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void handleJoinClassroom()} disabled={isJoining || !classCodeInput.trim()}>
                {isJoining ? 'Joining...' : 'Join Class'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}

function SkeletonBar({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-md ${className}`} />
}
